export const HRA_RESULT_CATEGORIES = [

  "Cardiac Health",

  "Metabolic Risk",

  "Lifestyle & Activity",

  "Mental Wellbeing",

];



export const getOptionId = (o) => o?._id ?? o?.id ?? o?.optionId;



const optionPoints = (opt) => Number(opt?.points ?? opt?.score ?? 0);



const hasExplicitPoints = (opts) => opts.some((o) => optionPoints(o) > 0);



const rankMaxPoints = (opts) => (opts.length > 1 ? opts.length : 1);



const rankPoints = (opts, selectedIdx) =>

  opts.length > 1 ? opts.length - selectedIdx : 1;



export const normalizeCategory = (name) => {

  const n = (name || "General Health").trim();

  if (/cardiac|cardiovascular|heart/i.test(n)) return "Cardiac Health";

  if (/metabolic|diabetes|glucose/i.test(n)) return "Metabolic Risk";

  if (/lifestyle|activity|exercise|diet/i.test(n)) return "Lifestyle & Activity";

  if (/mental|stress|sleep|wellbeing|well-being/i.test(n)) return "Mental Wellbeing";

  return "Cardiac Health";

};



export function groupIntoFixedCategories(questions) {

  const buckets = Object.fromEntries(HRA_RESULT_CATEGORIES.map((name) => [name, []]));

  questions.forEach((q) => {

    buckets[normalizeCategory(q.hraCategoryName)].push(q);

  });

  return HRA_RESULT_CATEGORIES.map((name) => ({ name, questions: buckets[name] }));

}



export const deriveRiskLevel = (wellnessScore, apiLevel) => {

  if (apiLevel && !/unknown/i.test(apiLevel)) return apiLevel;

  if (wellnessScore >= 80) return "Low";

  if (wellnessScore >= 60) return "Moderate";

  return "High";

};



export const defaultRiskMessage = (wellnessScore) => {

  if (wellnessScore >= 80) {

    return "Your score indicates generally good health. Keep up your current wellness habits.";

  }

  if (wellnessScore >= 60) {

    return "Your score indicates generally good health, but identifies specific areas for improvement, particularly concerning metabolic markers.";

  }

  return "Your responses suggest several areas that would benefit from closer attention and follow-up with a healthcare provider.";

};



const categoryAverage = (categoryScores) =>

  Math.round(

    HRA_RESULT_CATEGORIES.reduce((s, c) => s + (categoryScores[c] || 0), 0) /

      HRA_RESULT_CATEGORIES.length

  );



const parseApiWellness = (apiData) => {

  const apiTotal = Number(apiData.totalPoints ?? apiData.earnedPoints);

  const apiMax = Number(apiData.maxPoints ?? apiData.totalMaxPoints);

  if (Number.isFinite(apiTotal) && Number.isFinite(apiMax) && apiMax > 0) {

    return Math.round((apiTotal / apiMax) * 100);

  }

  const apiScore = Number(apiData.wellnessScore ?? apiData.score ?? apiData.totalScore);

  if (Number.isFinite(apiScore) && apiScore >= 0 && apiScore <= 100) {

    return Math.round(apiScore);

  }

  return null;

};



export function computeHraScores(questions, answers, apiData = {}) {

  let earned = 0;

  let max = 0;

  const categoryEarned = {};

  const categoryMax = {};



  HRA_RESULT_CATEGORIES.forEach((cat) => {

    categoryEarned[cat] = 0;

    categoryMax[cat] = 0;

  });



  questions.forEach((q) => {

    const cat = normalizeCategory(q.hraCategoryName);

    const opts = q.options || [];

    const useRank = opts.length > 0 && !hasExplicitPoints(opts);

    const maxOpt = useRank ? rankMaxPoints(opts) : opts.length ? Math.max(0, ...opts.map(optionPoints)) : 0;

    max += maxOpt;

    categoryMax[cat] = (categoryMax[cat] || 0) + maxOpt;



    const selectedIdx = opts.findIndex((o) => getOptionId(o) === answers[q._id]);

    const selected = selectedIdx >= 0 ? opts[selectedIdx] : null;

    const pts = selected

      ? useRank

        ? rankPoints(opts, selectedIdx)

        : optionPoints(selected)

      : 0;

    earned += pts;

    categoryEarned[cat] = (categoryEarned[cat] || 0) + pts;

  });



  const categoryScores = {};

  HRA_RESULT_CATEGORIES.forEach((cat) => {

    const m = categoryMax[cat] || 0;

    categoryScores[cat] = m > 0 ? Math.round(((categoryEarned[cat] || 0) / m) * 100) : 0;

  });



  if (apiData.categoryPoints) {

    Object.entries(apiData.categoryPoints).forEach(([cat, pts]) => {

      const normalized = normalizeCategory(cat);

      if (HRA_RESULT_CATEGORIES.includes(normalized)) {

        const val = Math.min(100, Math.round(Number(pts) || 0));

        if (categoryScores[normalized] === 0 && val > 0) {

          categoryScores[normalized] = val;

        }

      }

    });

  }



  let wellnessScore = max > 0 ? Math.round((earned / max) * 100) : null;

  if (wellnessScore == null) {

    wellnessScore = parseApiWellness(apiData);

  }

  if (wellnessScore == null || (wellnessScore === 0 && earned === 0 && max === 0)) {

    const avg = categoryAverage(categoryScores);

    if (avg > 0) wellnessScore = avg;

  }

  if (wellnessScore == null) wellnessScore = 0;



  wellnessScore = Math.min(100, Math.max(0, wellnessScore));



  return {

    wellnessScore,

    score: wellnessScore,

    totalPoints: earned,

    categoryPoints: categoryScores,

    riskLevel: deriveRiskLevel(wellnessScore, apiData.riskLevel),

    riskMessage: apiData.riskMessage || defaultRiskMessage(wellnessScore),

    prescribedForCategories: apiData.prescribedForCategories || {},

  };

}

